/* eslint-disable import/extensions */
import AlarmIcon from 'assets/svg/alarm/alarm.svg'
import Grid from 'common/components/Grid'
import Image from 'common/components/Image'
import Skeleton from 'common/components/Skeleton'
import Typography from 'common/components/Typography'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import CountdownFormatter from 'lib/countdown-formatter'
import { GAUniversalEvent } from 'lib/google-analytics'
import GA4, { IItems } from 'lib/google-analytics-4'
import { useEffect, useState } from 'react'
import useInView from 'react-cool-inview'
import { useTranslation } from 'react-i18next'
import { IProduct } from 'services/Home/stores/models/DailyDealsModel/interface'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

import Carousel from '../components/product-carousel'

const Container = styled.div`
  width: ${theme.layout.containerWidth};
  margin: 50px auto 0;
`

const DailyDealsTitleContainer = styled.div`
  width: ${theme.layout.containerWidth};
  margin-bottom: 20px;
`

const TitleAndTimeContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

const Time = styled.div`
  width: 165px;
  height: 34px;
  margin-left: 15px;
  border-radius: 3px;
  background-image: linear-gradient(to bottom, #ff8e59, #fd3a52 115%);
  display: flex;
  justify-content: center;
  align-items: center;
`
const StyledAlarmIcon = styled(AlarmIcon)`
  margin: 10px 10px auto 0;
`

const ShowAllContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const ProductContainer = styled.div`
  width: ${theme.layout.containerWidth};
  height: 400px;
  background-color: ${(props) => props.backgroundColor};
  display: flex;
  border-radius: 6px;
  overflow-x: hidden;
`

const BannerContainer = styled.div`
  display: ${(props) =>
    props.dailyDealsBannerImage === '' ? 'none' : 'block'};
  width: 260px;
  height: 400px;
`

function DailyDeals(): JSX.Element {
  const { t, i18n } = useTranslation('home')
  const {
    activeDailyDeals,
    products,
    isLoading,
    isError,
  } = useStoreState((state) => state.dailyDeals)
  const { data: cartData } = useStoreState((state) => state.cart)

  const { getActiveDailyDeals } = useStoreActions(
    (actions) => actions.dailyDeals,
  )

  const { observe, inView } = useInView<HTMLDivElement>({
    onEnter: ({ unobserve }) => unobserve(),
  })

  const [countdownValue, setCountdownValue] = useState('')

  const ga = {
    showAll: {
      eventCategory: 'Daily deals section on home page',
      eventAction: 'Click lihat semua in daily deals home page',
      eventLabel: 'Click_link_lihat_semua_in_daily_deals_section',
    },
    listDailyDeals: {
      eventCategory: 'Daily deals section on home page',
      eventAction: 'Click listing daily deals on home page',
      eventLabel: 'Click_listing_daily_deals_on_home_page',
    },
    product: {
      eventCategory: 'Daily deals page',
      eventAction: 'Click product on daily deals page',
      eventLabel: 'Click_product_on_daily_deals_page',
    },
  }

  const onClickShowAll = () => {
    GAUniversalEvent(
      ga.showAll.eventCategory,
      ga.showAll.eventAction,
      ga.showAll.eventLabel,
    )
  }

  const onClickArrowDailyDealsList = () => {
    GAUniversalEvent(
      ga.listDailyDeals.eventCategory,
      ga.listDailyDeals.eventAction,
      ga.listDailyDeals.eventLabel,
    )
  }

  const { trackEvent, trackSelectItem, trackOnLoadItem } = GA4()
  const onClickProductDailyDeals = ({ index, ...product }) => {
    trackSelectItem({
      itemId: product?.item_id,
      itemName: product?.name,
      discount: Number(product?.price) - Number(product?.discount),
      price: product?.price,
      quantity: product?.quantity,
      cartId: cartData?.id,
    })
    trackEvent({
      eventName: 'panel_tracking',
      eventCategory: 'Panel Tracking',
      params: {
        panel_name: 'Daily Deals',
        panel_position: `Slot - ${index + 1}`,
        panel_page_url: product.link,
        panel_page_title: product.name,
      },
    })
    GAUniversalEvent(
      ga.product.eventCategory,
      ga.product.eventAction,
      ga.product.eventLabel,
    )
  }

  const handleViewDailyItem = (items: IProduct[]) => {
    const itemList: IItems[] = items?.map((item: IProduct) => {
      return {
        itemId: item?.item_id,
        itemName: item?.name,
        coupon: null,
        currency: 'IDR',
        discount: Number(item?.price) - Number(item?.discount),
        itemBrand: '',
        itemCategory: '',
        itemCategory2: '',
        itemListId: '',
        itemListName: '',
        itemVariant: '',
        locationId: '',
        price: item?.price,
        quantity: item?.quantity,
      }
    })
    trackOnLoadItem({
      list: itemList,
    })
  }

  useEffect(() => {
    if (inView && !isError) {
      getActiveDailyDeals()
    }
  }, [getActiveDailyDeals, inView, isError, i18n.language])

  useEffect(() => {
    if (!isLoading && products.length !== 0) {
      handleViewDailyItem(products)
    }
  }, [isLoading, products])

  useEffect(() => {
    const countDown = setInterval(() => {
      if (activeDailyDeals[0]?.end_date) {
        setCountdownValue(
          CountdownFormatter(activeDailyDeals[0]?.end_date, {
            day: true,
            hours: true,
          }),
        )
      }
    }, 1000)
    return () => clearInterval(countDown)
  }, [activeDailyDeals])

  return (
    <div data-testid="daily-deals-test" ref={observe}>
      {isLoading && (
        <Container>
          <Skeleton width="100%" height="100%" />
        </Container>
      )}
      {activeDailyDeals?.length && !isLoading && !isError ? (
        <Container>
          <DailyDealsTitleContainer>
            <Grid templateColumn={2}>
              <>
                <TitleAndTimeContainer>
                  <Typography type="bh2">Daily Deals</Typography>
                  <Typography type="rparagraph" marginLeft="20px">
                    {t('ENDS_IN')}
                  </Typography>
                  <Time>
                    <StyledAlarmIcon />
                    <Typography type="bh3" color="white">
                      {countdownValue}
                    </Typography>
                  </Time>
                </TitleAndTimeContainer>
                <ShowAllContainer>
                  <a
                    href={`${WEB_DESKTOP_RALALI_URL}/dailydeals`}
                    target="#"
                    onClick={() => onClickShowAll()}
                  >
                    <Typography type="bparagraph" color="orange">
                      {t('SEE_ALL')}
                    </Typography>
                  </a>
                </ShowAllContainer>
              </>
            </Grid>
          </DailyDealsTitleContainer>
          <ProductContainer
            backgroundColor={
              activeDailyDeals[0]?.banners?.hexacode || '#ffd2bf'
            }
          >
            <BannerContainer
              dailyDealsBannerImage={
                activeDailyDeals[0]?.banners?.side
              }
            >
              <Image
                width="260px"
                height="400px"
                lazy
                src={activeDailyDeals[0]?.banners?.side}
                objectFit="cover"
                borderTopLeftRadius="6px"
                borderBottomLeftRadius="6px"
              />
            </BannerContainer>
            <Carousel
              products={products}
              isBannerExist={!!activeDailyDeals[0]?.banners?.side}
              onClickArrowDailyDealsList={() =>
                onClickArrowDailyDealsList()
              }
              onClickProductDailyDeals={onClickProductDailyDeals}
            />
          </ProductContainer>
        </Container>
      ) : null}
    </div>
  )
}

export default DailyDeals
